import type { SegmentOption } from "components/designV2/segmentcontroller/segmentcontroller";
import { useMemo } from "react";
import {
  buildTipsSummary,
  filterTipsBySummaryFilter,
} from "../logic/tips.v2.logic";
import type {
  EmptyStateCopy,
  TipsComputedData,
  TipsLabels,
  TipsTab,
  UseTipsComputedDataProps,
} from "../tips.component.types";

const capitalizeFirst = (value: string): string =>
  value.length > 0 ? value[0].toUpperCase() + value.slice(1) : value;

export const useTipsComputedData = (
  props: UseTipsComputedDataProps,
): TipsComputedData => {
  const activeSummaryFilter = props.activeSummaryFilter;
  const activeTab = props.activeTab;
  const currentDate = props.currentDate;
  const i18nLanguage = props.i18nLanguage;
  const t = props.t;
  const tips = props.tips;

  const tabOptions = useMemo<readonly SegmentOption<TipsTab>[]>(
    () => [
      {
        label: t("Tips.DistributeTab"),
        value: "tips",
      },
      {
        label: t("Tips.OverviewTab"),
        value: "monthlyReports",
      },
      {
        label: t("Tips.TransactionsTab"),
        value: "transactions",
      },
    ],
    [t],
  );

  const labels = useMemo<TipsLabels>(
    () => ({
      addAnyway: t("Tips.AddAnyway"),
      addAnywaysOrOnlyNewDates: t("Tips.AddAnywaysOrOnlyNewDates"),
      addDates: t("Tips.AddDates"),
      followingDatesAlreadyExist: t("Tips.FollowingDatesAlreadyExists"),
      loading: t("General.Loading"),
      nextMonth: t("Tips.NextMonth"),
      onlyNewDates: t("Tips.OnlyNewDates"),
      previousMonth: t("Tips.PreviousMonth"),
    }),
    [t],
  );

  const monthName = useMemo((): string => {
    const date = new Date(currentDate.year(), currentDate.monthValue() - 1, 1);
    const formatter = new Intl.DateTimeFormat(i18nLanguage, {
      month: "long",
    });

    return capitalizeFirst(formatter.format(date));
  }, [currentDate, i18nLanguage]);

  const emptyStateCopy = useMemo<EmptyStateCopy>(() => {
    if (activeTab === "transactions") {
      return {
        description: t("Tips.NoTransactionsForMonthDescription", {
          month: monthName,
        }),
        title: t("Tips.NoTransactionsForThisMonth"),
      };
    }

    if (activeTab === "monthlyReports") {
      return {
        description: t("Tips.NoMonthlyReportsForMonthDescription", {
          month: monthName,
        }),
        title: t("Tips.NoMonthlyReportsForThisMonth"),
      };
    }

    return {
      description: t("Tips.NoTipsForMonthDescription", {
        month: monthName,
      }),
      title: t("Tips.NoTipsForThisMonth"),
    };
  }, [activeTab, monthName, t]);

  const summary = useMemo(() => buildTipsSummary(tips), [tips]);
  const filteredTips = useMemo(
    () => filterTipsBySummaryFilter({ filter: activeSummaryFilter, tips }),
    [activeSummaryFilter, tips],
  );

  return {
    emptyStateCopy,
    filteredTips,
    hasTips: tips.length > 0,
    labels,
    summary,
    tabOptions,
  };
};
